import React, { useState, useEffect } from 'react';
import { PageHeader, LoadingSpinner } from '@/components/Layout';
import { api } from '@/services/api';
import clsx from 'clsx';

// Types
interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  priority: string;
  status: string;
  order_number?: string;
  rep_first_name?: string;
  rep_last_name?: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface Message {
  id: string;
  sender_type: string;
  sender_name: string;
  message: string;
  created_at: string;
}

interface TicketDetail extends Ticket {
  messages: Message[];
  dealer_company?: string;
  dealer_first_name?: string;
  dealer_last_name?: string;
}

interface Order {
  id: string;
  order_number: string;
  status: string;
  total_amount: number;
}

const CATEGORIES = [
  { value: 'order_issue', label: 'Order Issue' },
  { value: 'product_question', label: 'Product Question' },
  { value: 'shipping', label: 'Shipping' },
  { value: 'warranty', label: 'Warranty' },
  { value: 'technical', label: 'Technical' },
  { value: 'other', label: 'Other' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-500/20 text-gray-400',
  medium: 'bg-blue-500/20 text-blue-400',
  high: 'bg-orange-500/20 text-orange-400',
  urgent: 'bg-red-500/20 text-red-400',
};

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelativeTime(dateString: string) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateString);
}

// New Ticket Modal
function NewTicketModal({
  isOpen,
  onClose,
  onCreated,
  orders,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  orders: Order[];
}) {
  const [form, setForm] = useState({
    subject: '',
    category: 'other',
    priority: 'medium',
    description: '',
    order_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await api.post('/support/tickets', {
        ...form,
        order_id: form.order_id || undefined,
      });
      onCreated();
      onClose();
      setForm({
        subject: '',
        category: 'other',
        priority: 'medium',
        description: '',
        order_id: '',
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-keder-dark border border-keder-gray rounded-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-keder-gray">
          <h2 className="text-xl font-semibold text-white">Create Support Ticket</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Subject</label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full bg-keder-gray border border-keder-gray rounded-lg px-3 py-2 text-white focus:outline-none focus:border-keder-yellow"
              placeholder="Brief description of your issue"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-keder-gray border border-keder-gray rounded-lg px-3 py-2 text-white focus:outline-none focus:border-keder-yellow"
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
                className="w-full bg-keder-gray border border-keder-gray rounded-lg px-3 py-2 text-white focus:outline-none focus:border-keder-yellow"
              >
                {PRIORITIES.map((pri) => (
                  <option key={pri.value} value={pri.value}>
                    {pri.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {orders.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Related Order (optional)
              </label>
              <select
                value={form.order_id}
                onChange={(e) => setForm({ ...form, order_id: e.target.value })}
                className="w-full bg-keder-gray border border-keder-gray rounded-lg px-3 py-2 text-white focus:outline-none focus:border-keder-yellow"
              >
                <option value="">No related order</option>
                {orders.map((order) => (
                  <option key={order.id} value={order.id}>
                    {order.order_number} - {order.status}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={5}
              className="w-full bg-keder-gray border border-keder-gray rounded-lg px-3 py-2 text-white focus:outline-none focus:border-keder-yellow resize-none"
              placeholder="Please describe your issue in detail..."
              required
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-keder-yellow text-black font-medium rounded-lg hover:bg-keder-yellow/90 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Ticket Detail Modal
function TicketDetailModal({
  ticketId,
  onClose,
  onUpdated,
}: {
  ticketId: string | null;
  onClose: () => void;
  onUpdated: () => void;
}) {
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (ticketId) {
      loadTicket();
    }
  }, [ticketId]);

  const loadTicket = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/support/tickets/${ticketId}`);
      setTicket(response.data);
    } catch (err) {
      console.error('Failed to load ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !ticketId) return;

    setSending(true);
    try {
      await api.post(`/support/tickets/${ticketId}/messages`, {
        message: newMessage,
      });
      setNewMessage('');
      loadTicket();
      onUpdated();
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  if (!ticketId) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-keder-dark border border-keder-gray rounded-xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
        {loading ? (
          <div className="flex items-center justify-center p-12">
            <LoadingSpinner />
          </div>
        ) : ticket ? (
          <>
            {/* Header */}
            <div className="p-6 border-b border-keder-gray shrink-0">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-gray-400 text-sm">{ticket.ticket_number}</span>
                    <span
                      className={clsx(
                        'px-2 py-0.5 rounded text-xs font-medium border',
                        STATUS_COLORS[ticket.status]
                      )}
                    >
                      {ticket.status.replace('_', ' ')}
                    </span>
                    <span
                      className={clsx(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        PRIORITY_COLORS[ticket.priority]
                      )}
                    >
                      {ticket.priority}
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-white">{ticket.subject}</h2>
                  {ticket.order_number && (
                    <p className="text-sm text-gray-400 mt-1">Order: {ticket.order_number}</p>
                  )}
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-white p-1"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {ticket.rep_first_name && (
                <p className="text-sm text-gray-400 mt-2">
                  Assigned to: {ticket.rep_first_name} {ticket.rep_last_name}
                </p>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {ticket.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={clsx(
                    'rounded-lg p-4',
                    msg.sender_type === 'dealer'
                      ? 'bg-keder-yellow/10 border border-keder-yellow/20 ml-8'
                      : msg.sender_type === 'system'
                      ? 'bg-gray-700/50 text-center text-sm text-gray-400'
                      : 'bg-keder-gray border border-keder-gray mr-8'
                  )}
                >
                  {msg.sender_type !== 'system' && (
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-white text-sm">
                        {msg.sender_name || (msg.sender_type === 'dealer' ? 'You' : 'Support')}
                      </span>
                      <span className="text-xs text-gray-400">{formatRelativeTime(msg.created_at)}</span>
                    </div>
                  )}
                  <p className={clsx('whitespace-pre-wrap', msg.sender_type === 'system' ? '' : 'text-gray-300')}>
                    {msg.message}
                  </p>
                </div>
              ))}
            </div>

            {/* Reply Form */}
            {ticket.status !== 'closed' && (
              <form onSubmit={handleSendMessage} className="p-4 border-t border-keder-gray shrink-0">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your reply..."
                    className="flex-1 bg-keder-gray border border-keder-gray rounded-lg px-4 py-2 text-white focus:outline-none focus:border-keder-yellow"
                  />
                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim()}
                    className="px-4 py-2 bg-keder-yellow text-black font-medium rounded-lg hover:bg-keder-yellow/90 transition-colors disabled:opacity-50"
                  >
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </>
        ) : (
          <div className="p-12 text-center text-gray-400">Failed to load ticket</div>
        )}
      </div>
    </div>
  );
}

// Main Support Page
export function SupportPage() {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = {};
      if (filter !== 'all') {
        params.status = filter;
      }

      const [ticketsRes, ordersRes] = await Promise.all([
        api.get('/support/tickets/my', { params }),
        api.get('/support/dealer-orders'),
      ]);

      setTickets(ticketsRes.data.tickets || []);
      setOrders(ordersRes.data || []);
    } catch (err) {
      console.error('Failed to load support data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTicketCreated = () => {
    loadData();
  };

  const handleTicketUpdated = () => {
    loadData();
  };

  return (
    <div>
      <PageHeader
        title="Support"
        subtitle="Get help with your orders and products"
        actions={
          <button
            onClick={() => setShowNewTicket(true)}
            className="px-4 py-2 bg-keder-yellow text-black font-medium rounded-lg hover:bg-keder-yellow/90 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Ticket
          </button>
        }
      />

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['all', 'open', 'in_progress', 'resolved', 'closed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              filter === status
                ? 'bg-keder-yellow text-black'
                : 'bg-keder-gray text-gray-400 hover:text-white'
            )}
          >
            {status === 'all' ? 'All' : status.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Tickets List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : tickets.length === 0 ? (
        <div className="bg-keder-dark border border-keder-gray rounded-xl p-12 text-center">
          <svg
            className="w-16 h-16 mx-auto text-gray-600 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
          <h3 className="text-lg font-medium text-white mb-2">No tickets found</h3>
          <p className="text-gray-400 mb-6">
            {filter === 'all'
              ? "You haven't created any support tickets yet."
              : `No ${filter.replace('_', ' ')} tickets found.`}
          </p>
          <button
            onClick={() => setShowNewTicket(true)}
            className="px-6 py-2 bg-keder-yellow text-black font-medium rounded-lg hover:bg-keder-yellow/90 transition-colors"
          >
            Create Your First Ticket
          </button>
        </div>
      ) : (
        <div className="bg-keder-dark border border-keder-gray rounded-xl overflow-hidden">
          <table className="w-full">
            <thead className="bg-keder-gray/50">
              <tr>
                <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Ticket</th>
                <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Category</th>
                <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Priority</th>
                <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Status</th>
                <th className="text-left text-sm font-medium text-gray-400 px-4 py-3">Updated</th>
                <th className="text-center text-sm font-medium text-gray-400 px-4 py-3">Messages</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-keder-gray">
              {tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  onClick={() => setSelectedTicketId(ticket.id)}
                  className="hover:bg-keder-gray/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-white font-medium">{ticket.subject}</p>
                      <p className="text-sm text-gray-400">{ticket.ticket_number}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-300 text-sm capitalize">
                      {ticket.category.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-medium capitalize',
                        PRIORITY_COLORS[ticket.priority]
                      )}
                    >
                      {ticket.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-medium capitalize border',
                        STATUS_COLORS[ticket.status]
                      )}
                    >
                      {ticket.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-gray-400 text-sm">{formatRelativeTime(ticket.updated_at)}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-keder-gray text-white text-xs">
                      {ticket.message_count}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <NewTicketModal
        isOpen={showNewTicket}
        onClose={() => setShowNewTicket(false)}
        onCreated={handleTicketCreated}
        orders={orders}
      />

      <TicketDetailModal
        ticketId={selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
        onUpdated={handleTicketUpdated}
      />
    </div>
  );
}
